import { createApp } from "@backstage/app-defaults";
import { AppRouter, FlatRoutes } from "@backstage/core-app-api";
import {
	AlertDisplay,
	OAuthRequestDialog,
	SignInPage,
} from "@backstage/core-components";
import {
	configApiRef,
	googleAuthApiRef,
	useApi,
} from "@backstage/core-plugin-api";
import { ApiExplorerPage, apiDocsPlugin } from "@backstage/plugin-api-docs";
import {
	CatalogEntityPage,
	CatalogIndexPage,
	catalogPlugin,
} from "@backstage/plugin-catalog";
import { catalogEntityCreatePermission } from "@backstage/plugin-catalog-common/alpha";
import { CatalogGraphPage } from "@backstage/plugin-catalog-graph";
import {
	CatalogImportPage,
	catalogImportPlugin,
} from "@backstage/plugin-catalog-import";
import { NotificationsPage } from "@backstage/plugin-notifications";
import { orgPlugin } from "@backstage/plugin-org";
import { RequirePermission } from "@backstage/plugin-permission-react";
import { ScaffolderPage, scaffolderPlugin } from "@backstage/plugin-scaffolder";
import { SearchPage } from "@backstage/plugin-search";
import { SignalsDisplay } from "@backstage/plugin-signals";
import {
	TechDocsIndexPage,
	TechDocsReaderPage,
	techdocsPlugin,
} from "@backstage/plugin-techdocs";
import { ReportIssue } from "@backstage/plugin-techdocs-module-addons-contrib";
import { TechDocsAddons } from "@backstage/plugin-techdocs-react";
import { UserSettingsPage } from "@backstage/plugin-user-settings";
import { Navigate, Route } from "react-router-dom";
import { apis, zendosAuthApiRef } from "./apis";
import { entityPage } from "./components/catalog/EntityPage";
import { Root } from "./components/Root";
import { searchPage } from "./components/search/SearchPage";

// @backstage/app-defaults automatically provides auth API factory and other defaults
// Custom apis are merged with defaults
const app = createApp({
	apis,
	featureFlags: [
		{
			pluginId: "app",
			name: "enable-example-feature",
			description: "Example feature flag for A/B testing or gradual rollout",
		},
	],
	bindRoutes({ bind }) {
		bind(catalogPlugin.externalRoutes, {
			createComponent: scaffolderPlugin.routes.root,
			viewTechDoc: techdocsPlugin.routes.docRoot,
			createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
		});
		bind(apiDocsPlugin.externalRoutes, {
			registerApi: catalogImportPlugin.routes.importPage,
		});
		bind(scaffolderPlugin.externalRoutes, {
			registerComponent: catalogImportPlugin.routes.importPage,
			viewTechDoc: techdocsPlugin.routes.docRoot,
		});
		bind(orgPlugin.externalRoutes, {
			catalogIndex: catalogPlugin.routes.catalogIndex,
		});
	},
	components: {
		// Keep guest auth for localhost development; render selects the deployed provider.
		SignInPage: (props) => {
			const configApi = useApi(configApiRef);
			const provider =
				configApi.getOptionalString("auth.defaultProvider") ?? "authentik";

			return (
				<SignInPage
					{...props}
					auto
					providers={
						window.location.hostname === "localhost"
							? ["guest"]
							: provider === "google"
								? [
										{
											id: "google-auth-provider",
											title: "Google",
											message: "Sign in using Google",
											apiRef: googleAuthApiRef,
										},
									]
								: [
										{
											id: "zendos-auth-provider",
											title: "Zendos login",
											message: "Sign in using Zendos login",
											apiRef: zendosAuthApiRef,
										},
									]
					}
				/>
			);
		},
	},
});

const routes = (
	<FlatRoutes>
		<Route path="/" element={<Navigate to="catalog" />} />
		<Route path="/catalog" element={<CatalogIndexPage />} />
		<Route
			path="/catalog/:namespace/:kind/:name"
			element={<CatalogEntityPage />}
		>
			{entityPage}
		</Route>
		<Route path="/docs" element={<TechDocsIndexPage />} />
		<Route
			path="/docs/:namespace/:kind/:name/*"
			element={<TechDocsReaderPage />}
		>
			<TechDocsAddons>
				<ReportIssue />
			</TechDocsAddons>
		</Route>
		<Route path="/create" element={<ScaffolderPage />} />
		<Route path="/api-docs" element={<ApiExplorerPage />} />
		<Route
			path="/catalog-import"
			element={
				<RequirePermission permission={catalogEntityCreatePermission}>
					<CatalogImportPage />
				</RequirePermission>
			}
		/>
		<Route path="/search" element={<SearchPage />}>
			{searchPage}
		</Route>
		<Route path="/settings" element={<UserSettingsPage />} />
		<Route path="/catalog-graph" element={<CatalogGraphPage />} />
		<Route path="/notifications" element={<NotificationsPage />} />
	</FlatRoutes>
);

export default app.createRoot(
	<>
		<AlertDisplay />
		<OAuthRequestDialog />
		<SignalsDisplay />
		<AppRouter>
			<Root>{routes}</Root>
		</AppRouter>
	</>,
);
